import { baseApi } from "./baseApi";
import { API_BASE_URL } from "../../config/env";

// agent status
interface AgentStatus {
  name: string;
  status: "online" | "in_chat" | "idle" | "busy" | "away" | "offline" | "out_of_tokens";
  activeEvents?: number;
  uptime?: number;
  current_activity?: string;
  // which contact or channel the agent is currently in (null when not in a chat)
  presence?: string | null;
}

// voice-note metadata the client renders (audio player + the playful fun summary). The verbatim
// transcript is redacted server-side — other users only ever get the fun summary.
interface ChatVoice {
  audioUrl: string;
  durationMs?: number;
  funSummary?: string;
}

// chat history
interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  contact?: string;
  channel?: string;
  // shared message id — the key emoji reactions attach to. May be absent on legacy rows.
  mid?: string;
  // present when this message was sent as a voice note.
  voice?: ChatVoice;
}

interface ChatHistoryResponse {
  messages: ChatMessage[];
  contact: string;
  total?: number;
}

// group chat
interface GroupMessageSender {
  type: "user" | "agent";
  username: string;
  backendUserId?: string;
}

interface GroupMessage {
  _id: string;
  channel: string;
  sender: GroupMessageSender;
  content: string;
  createdAt: string;
  // shared message id — the key emoji reactions attach to (backfilled from _id on legacy rows)
  mid: string;
  // present when this message was sent as a voice note.
  voice?: ChatVoice;
}

// per-user playback state for a voice clip (hear-once + one replay).
interface VoicePlayState {
  voiceId: string;
  plays: number;
  locked: boolean;
}

interface VoiceListenStateResponse {
  states: Record<string, VoicePlayState>;
  maxPlays: number;
}

interface GroupHistoryResponse {
  messages: GroupMessage[];
  hasMore: boolean;
}

// announcements
interface AnnouncementSection {
  type: string;
  highlight: string;
}

interface Announcement {
  _id: string;
  agentName: string;
  title: string;
  content: string;
  sections?: AnnouncementSection[];
  createdAt: string;
  telegramSent: boolean;
}

interface AnnouncementsResponse {
  announcements: Announcement[];
  hasMore: boolean;
}

// blog articles — the public feed carries the sitebot's posts and human ones
// (admin/`writer` role, authored at /blog/new), distinguished by `author`.
interface Article {
  _id: string;
  agentName?: string;
  authorType: "agent" | "user";
  authorUsername?: string;
  author: { type: "agent" | "user"; name: string; label: string };
  slug: string;
  title: string;
  subtitle?: string;
  excerpt?: string;
  content?: string;
  coverImageUrl?: string;
  category: string;
  tags: string[];
  publishedAt: string;
  updatedAt?: string;
  wayback?: { status: string; snapshotUrl?: string; capturedAt?: string };
  // present only when the article is a chapter of a book
  bookId?: string;
  chapterNumber?: number;
  book?: BookSummary;
  chapterIndex?: number;
  previousChapter?: ChapterBrief | null;
  nextChapter?: ChapterBrief | null;
}

interface ArticlesResponse {
  articles: Article[];
  hasMore: boolean;
}

// books — a topic whose chapters are articles, published one at a time
interface BookSummary {
  _id: string;
  slug: string;
  title: string;
  subtitle?: string;
  excerpt?: string;
  description?: string;
  coverImageUrl?: string;
  authorUsername?: string;
  tags: string[];
  complete: boolean;
  publishedAt?: string;
  chapterCount: number;
}

interface Book extends BookSummary {
  chapters: Article[];
}

interface BooksResponse {
  books: BookSummary[];
}

interface ChapterBrief {
  _id: string;
  slug: string;
  title: string;
  chapterNumber?: number;
}

// per-user read cursors (server-side, browser-independent)
interface ChatReadStateResponse {
  personalLastReadAt: string | null;
  groupLastReadAt: string | null;
}

interface MarkChatReadArgs {
  personal?: string | boolean;
  group?: string | boolean;
}

// read receipts (last-seen) — one per general-channel member with a read cursor.
// the emoji's position is derived client-side: it sits under the newest message whose
// createdAt <= groupLastReadAt (see ReadReceipts.tsx).
interface GroupReceipt {
  username: string;
  userEmoji: string;
  groupLastReadAt: string | null;
  groupDeliveredAt: string | null;
}

// personal-chat read/delivered cursors: the requesting user's own + the bot's. The bot's
// cursors drive the "delivered / seen by sitebot" indicator on the user's own messages.
interface PersonalReceipts {
  user: { personalLastReadAt: string | null; personalDeliveredAt: string | null };
  bot: { personalLastReadAt: string | null; personalDeliveredAt: string | null };
}

// presence
interface PresenceUser {
  username: string;
  chatType: string;
  lastSeen: string;
  typing?: boolean;
  /** profile emoji — empty string if the user hasn't set one yet */
  userEmoji?: string;
}

interface PresenceResponse {
  users: PresenceUser[];
  count: number;
}

// presence SSE events
interface PresenceEvent {
  type: "join" | "leave" | "typing" | "typing_stop" | "snapshot";
  username?: string;
  chatType?: string;
  users?: PresenceUser[];
  /** only present on `join` events */
  userEmoji?: string;
}

// message reactions — one entry per (mid, emoji)
interface ReactionGroup {
  mid: string;
  emoji: string;
  count: number;
  // did the requesting user react with this emoji
  mine: boolean;
  // did the agent (sitebot) react with this emoji
  agentReacted: boolean;
  // usernames of human reactors (agent shown via agentReacted)
  reactors: string[];
}

interface ReactionsResponse {
  reactions: ReactionGroup[];
}

// live reaction event over the chat SSE stream
interface ReactionStreamEvent {
  type: string;
  chatType?: "general" | "personal";
  mid?: string;
  emoji?: string;
  reactor?: string;
  reactorType?: "user" | "agent";
  removed?: boolean;
}

// apply a single reaction add/remove to a reactions list in place. idempotent — guards against
// double-counting the same reactor so optimistic updates + SSE + polling can't drift.
function applyReactionDelta(
  list: ReactionGroup[],
  ev: { mid: string; emoji: string; reactor: string; reactorType: "user" | "agent"; removed: boolean },
  me: string,
): void {
  const idx = list.findIndex((g) => g.mid === ev.mid && g.emoji === ev.emoji);
  if (ev.removed) {
    if (idx === -1) return;
    const g = list[idx];
    if (ev.reactorType === "agent") {
      if (g.agentReacted) {
        g.agentReacted = false;
        g.count = Math.max(0, g.count - 1);
      }
    } else if (g.reactors.includes(ev.reactor)) {
      g.reactors = g.reactors.filter((r) => r !== ev.reactor);
      g.count = Math.max(0, g.count - 1);
      if (ev.reactor === me) g.mine = false;
    }
    if (g.count <= 0) list.splice(idx, 1);
    return;
  }
  let g = idx === -1 ? undefined : list[idx];
  if (!g) {
    g = { mid: ev.mid, emoji: ev.emoji, count: 0, mine: false, agentReacted: false, reactors: [] };
    list.push(g);
  }
  if (ev.reactorType === "agent") {
    if (!g.agentReacted) {
      g.agentReacted = true;
      g.count += 1;
    }
  } else if (!g.reactors.includes(ev.reactor)) {
    g.reactors.push(ev.reactor);
    g.count += 1;
    if (ev.reactor === me) g.mine = true;
  }
}

export const agentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // agent status — initial fetch via REST, then live updates via SSE
    getAgentStatus: builder.query<AgentStatus, void>({
      query: () => "/agent/status",
      async onCacheEntryAdded(
        _arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        let source: EventSource | null = null;
        try {
          await cacheDataLoaded;

          source = new EventSource(`${API_BASE_URL}/agent/status/stream`);

          source.onmessage = (event) => {
            try {
              const data = JSON.parse(event.data) as AgentStatus;
              updateCachedData((draft) => {
                Object.assign(draft, data);
              });
            } catch {
              // ignore parse errors
            }
          };
          // SSE auto-reconnects on error — nothing to do here

          await cacheEntryRemoved;
        } catch {
          // cache entry removed before initial query resolved
        }
        source?.close();
      },
    }),

    // personal chat
    sendAgentMessage: builder.mutation<{ success: boolean }, { text: string }>({
      query: (body) => ({
        url: "/agent/chat/send",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AgentChat"],
    }),

    getAgentChatHistory: builder.query<ChatHistoryResponse, { limit?: number }>({
      query: ({ limit = 50 }) => `/agent/chat/history?limit=${limit}`,
      providesTags: ["AgentChat"],
    }),

    // record a voice message into the group or personal chat. multipart upload — the backend
    // transcribes, cleans, builds the fun summary, then routes it like a normal message.
    sendVoiceMessage: builder.mutation<
      { success: boolean; mid: string },
      { audio: Blob; target: "group" | "personal"; durationMs?: number; channel?: string }
    >({
      query: ({ audio, target, durationMs, channel }) => {
        const form = new FormData();
        const ext = audio.type.includes("mp4") ? "m4a" : audio.type.includes("ogg") ? "ogg" : "webm";
        form.append("audio", audio, `voice.${ext}`);
        form.append("target", target);
        if (durationMs != null) form.append("durationMs", String(Math.round(durationMs)));
        if (channel) form.append("channel", channel);
        return { url: "/agent/voice/send", method: "POST", body: form };
      },
      invalidatesTags: (_r, _e, arg) => (arg.target === "group" ? ["AgentGroupChat"] : ["AgentChat"]),
    }),

    // per-user playback state for the given voice clip ids (hear-once + one replay).
    getVoiceListenState: builder.query<VoiceListenStateResponse, string[]>({
      query: (ids) => `/voice/listen-state?ids=${encodeURIComponent(ids.join(","))}`,
    }),

    // record that the current user started playing a voice clip; returns the new play count.
    recordVoiceHeard: builder.mutation<
      { voiceId: string; plays: number; locked: boolean; maxPlays: number },
      { voiceId: string }
    >({
      query: (body) => ({ url: "/voice/heard", method: "POST", body }),
    }),

    // server-side read cursors — source of truth for unread, shared across browsers
    getChatReadState: builder.query<ChatReadStateResponse, void>({
      query: () => "/agent/chat/read-state",
      providesTags: ["AgentChatReadState"],
    }),

    markChatRead: builder.mutation<{ success: boolean }, MarkChatReadArgs>({
      query: (body) => ({
        url: "/agent/chat/read-state",
        method: "POST",
        body,
      }),
      // optimistically advance the cached cursor so unread clears immediately
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          agentApi.util.updateQueryData("getChatReadState", undefined, (draft) => {
            const now = new Date().toISOString();
            if (arg.personal !== undefined && arg.personal !== false) {
              draft.personalLastReadAt =
                typeof arg.personal === "string" ? arg.personal : now;
            }
            if (arg.group !== undefined && arg.group !== false) {
              draft.groupLastReadAt =
                typeof arg.group === "string" ? arg.group : now;
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    // group chat. `channel` is optional: omit/"general" = normal group chat, "aotd" = the
    // pre-unlock waiting room. Existing callers pass only `{ text }` and are unaffected — an
    // absent channel is simply not serialised into the POST body.
    sendGroupMessage: builder.mutation<{ success: boolean }, { text: string; channel?: string }>({
      query: (body) => ({
        url: "/agent/group/send",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AgentGroupChat"],
    }),

    getGroupHistory: builder.query<GroupHistoryResponse, { limit?: number; before?: string }>({
      query: ({ limit = 50, before }) => {
        let url = `/agent/group/history?limit=${limit}`;
        if (before) url += `&before=${before}`;
        return url;
      },
      providesTags: ["AgentGroupChat"],
    }),

    // last-seen read receipts for the general channel (bare array). Polled alongside the
    // group-history poll; also refetched when a new group message invalidates the tag.
    getGroupReceipts: builder.query<GroupReceipt[], void>({
      query: () => "/agent/group/receipts",
      providesTags: ["AgentGroupChat"],
    }),

    // toggle the current user's reaction on a general-chat message. Optimistic so the chip
    // flips instantly; the server response + poll reconcile.
    reactGroupMessage: builder.mutation<
      { success: boolean; active: boolean },
      { mid: string; emoji: string }
    >({
      query: (body) => ({ url: "/agent/group/react", method: "POST", body }),
      async onQueryStarted({ mid, emoji }, { dispatch, getState, queryFulfilled }) {
        const me = (getState() as { auth?: { username?: string } }).auth?.username ?? "";
        const patch = dispatch(
          agentApi.util.updateQueryData("getGroupReactions", undefined, (draft) => {
            const existing = draft.reactions.find((g) => g.mid === mid && g.emoji === emoji);
            const willRemove = !!existing?.mine;
            applyReactionDelta(
              draft.reactions,
              { mid, emoji, reactor: me, reactorType: "user", removed: willRemove },
              me,
            );
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    // all reactions on the general channel, grouped by (mid, emoji). Live via SSE + polled.
    getGroupReactions: builder.query<ReactionsResponse, void>({
      query: () => "/agent/group/reactions",
      providesTags: ["AgentGroupChat"],
      async onCacheEntryAdded(
        _arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved, getState },
      ) {
        let source: EventSource | null = null;
        try {
          await cacheDataLoaded;
          const state = getState() as { auth?: { token?: string; username?: string } };
          const token = state.auth?.token;
          const me = state.auth?.username ?? "";
          if (token) {
            source = new EventSource(
              `${API_BASE_URL}/agent/chat/stream?access_token=${encodeURIComponent(token)}`,
            );
            source.onmessage = (event) => {
              try {
                const ev = JSON.parse(event.data) as ReactionStreamEvent;
                if (ev.type !== "reaction" || ev.chatType !== "general") return;
                if (!ev.mid || !ev.emoji || !ev.reactorType || !ev.reactor) return;
                // our own user reaction is already applied optimistically — skip the echo
                if (ev.reactorType === "user" && ev.reactor === me) return;
                updateCachedData((draft) => {
                  applyReactionDelta(
                    draft.reactions,
                    {
                      mid: ev.mid as string,
                      emoji: ev.emoji as string,
                      reactor: ev.reactor as string,
                      reactorType: ev.reactorType as "user" | "agent",
                      removed: !!ev.removed,
                    },
                    me,
                  );
                });
              } catch {
                // ignore parse errors
              }
            };
          }
          await cacheEntryRemoved;
        } catch {
          // cache entry removed before initial load
        }
        source?.close();
      },
    }),

    // personal-chat read/delivered cursors (current user + bot). Drives the own-message
    // delivered/seen indicator in PersonalChat.
    getPersonalReceipts: builder.query<PersonalReceipts, void>({
      query: () => "/agent/chat/receipts",
      providesTags: ["AgentChat"],
    }),

    // toggle the current user's reaction on a personal-chat message (optimistic).
    reactPersonalMessage: builder.mutation<
      { success: boolean; active: boolean },
      { mid: string; emoji: string }
    >({
      query: (body) => ({ url: "/agent/chat/react", method: "POST", body }),
      async onQueryStarted({ mid, emoji }, { dispatch, getState, queryFulfilled }) {
        const me = (getState() as { auth?: { username?: string } }).auth?.username ?? "";
        const patch = dispatch(
          agentApi.util.updateQueryData("getPersonalReactions", undefined, (draft) => {
            const existing = draft.reactions.find((g) => g.mid === mid && g.emoji === emoji);
            const willRemove = !!existing?.mine;
            applyReactionDelta(
              draft.reactions,
              { mid, emoji, reactor: me, reactorType: "user", removed: willRemove },
              me,
            );
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    // all reactions in the current user's personal thread, grouped by (mid, emoji).
    getPersonalReactions: builder.query<ReactionsResponse, void>({
      query: () => "/agent/chat/reactions",
      providesTags: ["AgentChat"],
      async onCacheEntryAdded(
        _arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved, getState },
      ) {
        let source: EventSource | null = null;
        try {
          await cacheDataLoaded;
          const state = getState() as { auth?: { token?: string; username?: string } };
          const token = state.auth?.token;
          const me = state.auth?.username ?? "";
          if (token) {
            source = new EventSource(
              `${API_BASE_URL}/agent/chat/stream?access_token=${encodeURIComponent(token)}`,
            );
            source.onmessage = (event) => {
              try {
                const ev = JSON.parse(event.data) as ReactionStreamEvent;
                if (ev.type !== "reaction" || ev.chatType !== "personal") return;
                if (!ev.mid || !ev.emoji || !ev.reactorType || !ev.reactor) return;
                if (ev.reactorType === "user" && ev.reactor === me) return;
                updateCachedData((draft) => {
                  applyReactionDelta(
                    draft.reactions,
                    {
                      mid: ev.mid as string,
                      emoji: ev.emoji as string,
                      reactor: ev.reactor as string,
                      reactorType: ev.reactorType as "user" | "agent",
                      removed: !!ev.removed,
                    },
                    me,
                  );
                });
              } catch {
                // ignore parse errors
              }
            };
          }
          await cacheEntryRemoved;
        } catch {
          // cache entry removed before initial load
        }
        source?.close();
      },
    }),

    // announcements
    getAnnouncements: builder.query<AnnouncementsResponse, { limit?: number; before?: string }>({
      query: ({ limit = 20, before }) => {
        let url = `/agent/announcements?limit=${limit}`;
        if (before) url += `&before=${before}`;
        return url;
      },
      providesTags: ["AgentAnnouncements"],
    }),

    // blog articles
    getArticles: builder.query<
      ArticlesResponse,
      { limit?: number; category?: string; author?: "agent" | "user" }
    >({
      query: ({ limit = 20, category, author }) => {
        let url = `/agent/blog?limit=${limit}`;
        if (category) url += `&category=${category}`;
        if (author) url += `&author=${author}`;
        return url;
      },
      providesTags: ["BlogArticles"],
    }),

    getArticle: builder.query<Article, string>({
      query: (slug) => `/agent/blog/${slug}`,
      providesTags: (_result, _err, slug) => [{ type: "BlogArticles", id: slug }],
    }),

    // books
    getBooks: builder.query<BooksResponse, void>({
      query: () => "/agent/blog/books",
      providesTags: ["BlogBooks"],
    }),

    getBook: builder.query<Book, string>({
      query: (slug) => `/agent/blog/books/${slug}`,
      providesTags: (_result, _err, slug) => [{ type: "BlogBooks", id: slug }],
    }),

    // presence
    sendPresenceHeartbeat: builder.mutation<{ success: boolean }, { chatType: string }>({
      query: (body) => ({
        url: "/agent/presence",
        method: "POST",
        body,
      }),
    }),

    leavePresence: builder.mutation<{ success: boolean }, void>({
      query: () => ({
        url: "/agent/presence",
        method: "DELETE",
      }),
    }),

    getPresence: builder.query<PresenceResponse, void>({
      query: () => "/agent/presence",
      providesTags: ["AgentPresence"],
      // live updates via SSE — initial GET gives snapshot, then SSE pushes deltas
      async onCacheEntryAdded(
        _arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        let source: EventSource | null = null;
        try {
          await cacheDataLoaded;

          source = new EventSource(`${API_BASE_URL}/agent/presence/stream`);

          source.onmessage = (event) => {
            try {
              const ev = JSON.parse(event.data) as PresenceEvent;

              updateCachedData((draft) => {
                if (ev.type === "snapshot" && ev.users) {
                  draft.users = ev.users;
                  draft.count = ev.users.length;
                  return;
                }

                if (ev.type === "join" && ev.username) {
                  if (!draft.users.find((u) => u.username === ev.username)) {
                    draft.users.push({
                      username: ev.username,
                      chatType: ev.chatType || "group",
                      lastSeen: new Date().toISOString(),
                      userEmoji: ev.userEmoji ?? "",
                    });
                    draft.count = draft.users.length;
                  }
                  return;
                }

                if (ev.type === "leave" && ev.username) {
                  draft.users = draft.users.filter((u) => u.username !== ev.username);
                  draft.count = draft.users.length;
                  return;
                }

                if (ev.type === "typing" && ev.username) {
                  const user = draft.users.find((u) => u.username === ev.username);
                  if (user) user.typing = true;
                  return;
                }

                if (ev.type === "typing_stop" && ev.username) {
                  const user = draft.users.find((u) => u.username === ev.username);
                  if (user) user.typing = false;
                  return;
                }
              });
            } catch {
              // ignore parse errors
            }
          };

          await cacheEntryRemoved;
        } catch {
          // cache entry removed before loaded
        }
        source?.close();
      },
    }),

    sendTypingSignal: builder.mutation<{ success: boolean }, { chatType: string }>({
      query: (body) => ({
        url: "/agent/presence/typing",
        method: "POST",
        body,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAgentStatusQuery,
  useSendAgentMessageMutation,
  useSendVoiceMessageMutation,
  useGetVoiceListenStateQuery,
  useRecordVoiceHeardMutation,
  useGetAgentChatHistoryQuery,
  useGetChatReadStateQuery,
  useMarkChatReadMutation,
  useSendGroupMessageMutation,
  useGetGroupHistoryQuery,
  useGetGroupReceiptsQuery,
  useGetPersonalReceiptsQuery,
  useReactGroupMessageMutation,
  useGetGroupReactionsQuery,
  useReactPersonalMessageMutation,
  useGetPersonalReactionsQuery,
  useGetAnnouncementsQuery,
  useGetArticlesQuery,
  useGetArticleQuery,
  useGetBooksQuery,
  useGetBookQuery,
  useSendPresenceHeartbeatMutation,
  useLeavePresenceMutation,
  useGetPresenceQuery,
  useSendTypingSignalMutation,
} = agentApi;

export type {
  AgentStatus,
  ChatMessage,
  ChatVoice,
  GroupMessage,
  VoicePlayState,
  GroupReceipt,
  PersonalReceipts,
  ReactionGroup,
  ReactionsResponse,
  Announcement,
  Article,
  Book,
  BookSummary,
  PresenceUser,
  PresenceEvent,
};
